/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import {
  DefaultCaps,
  Director,
  CapDataDesc,
  Stage,
  DefaultNavigators,
  StageDesc,
  CapNames,
  FloorKind,
  SceneCompass,
  FloorDesc,
} from '@sick-davinci/glow';
import {
  CrownCapNames,
  CrownHeightmapCap,
  CrownImageDesc,
  CrownViewCapData,
  CrownImageDecorationDesc,
  CrownViewPresentAddDesc,
} from '@sick-davinci/glow-crown';
import {
  CrownConnectionWebSocket,
  CrownProtocolMsgPack,
  LogLevel,
  MsgPackDecoder,
} from '@sick-davinci/device-communication';

import { addStatsWidget } from '@sick-davinci/glow-utils';
import { DropDown, TextField } from '@sick-davinci/basic-elements';

import { FileLoader, Vector3 } from 'three';

export const dependencies = [DropDown, TextField];  // avoid wepack removal

function isColorMap(name: string) {
  return name !== 'texture' && name !== 'intensity';
}

/**
 * Demo:    CrownHeightmapViewer
 * Description:
 *  This demo application displays a CrownHeightmap.
 *  Incoming (using Crown API / CrownView - Iface) device data is put on to the scene.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / WebSocket connection
 */
export const run = ((viewParent: HTMLElement) => {

  // ** setup the stage *
  const setup: StageDesc = {
    cameras: [
      {
        name: 'defaultCamera',
        from: [0, 1, 2],
        to: [0, 0, 0],
        pivot: [0, 0, 0],
        up: [0, 0, 1],
        fov: 50,
      },
    ],
    scenes: [{
        name: 'default',
     },
    ],
    viewports: [
      {
        scene: 'default',
        camera: 'defaultCamera',
        navigator: 'orbit',
        bgcolor: 0x393939,
      },
    ],
  };

  const stage = new Stage(viewParent, setup);

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {
    rotateAround: {
      up: new Vector3(0, 0, 1),
    },
    pan: {
      speed: .02,
    },
    straightAhead: {
      speed: .05,
    },
  });

  // * Add the scene compass for orientation
  SceneCompass.create(stage, 'default', 'defaultCamera');

  // Show the fps counter
  addStatsWidget(stage);

  // * Start displaying the scene
  stage.start();

  let lastHeightmap: CapDataDesc;
  let textureBackup: any;
  let decorationBackup: any;

  // * GUI handling *
  const colMapSel = document.getElementById('colormap') as DropDown;
  let colMap = colMapSel.value;
  colMapSel.addEventListener('change', ((evt) => {
    colMap = (evt as any).detail;
    if (!!lastHeightmap) {
      addHeightmapToScene(lastHeightmap);
    }
  }), true);

  const maxValIn = document.getElementById('maxval') as TextField;
  let maxVal = Number.parseInt(maxValIn.value, 10);
  maxValIn.addEventListener('change', ((evt) => {
    const v = (evt as any).detail;
    maxVal = Number.parseInt(v, 10);
    if (!!lastHeightmap) {
      addHeightmapToScene(lastHeightmap);
    }
  }), true);

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director); // addCapability for all default caps
  director.addCapability(new CrownHeightmapCap());

  // * Add some light so there is smth to see
  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0xffffff, intensity: 10 }, orientation: { position: [10, 10, 100] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });

  // * Add a floor for better orientation
  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 16,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, 0, -0.1],
      rotation: [Math.PI / 2, 0, 0],
    },
  } as CapDataDesc<FloorDesc>);

  // * Connect to the device via the ViewCrown using the provided ip and view-id
  const cc = new CrownConnectionWebSocket('127.0.0.1', 'crownMSGPACK', 3000);
  const cp = new CrownProtocolMsgPack();
  const view = new CrownViewCapData(cc, cp, '3D');

  // ** Set all necessary callback functions for the device connection
  // * During present calls the rendering should be stopped to avoid flickering *
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearTaggedObjects('clearable'));
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onConnectionChanged((connected) => {
    if (connected) {
       view.logMessage(LogLevel.INFO, 'Heightmap Viewer' + !connected ? 'dis-' : '' + 'connected.');
    }
  });
  // * actual payload handling *
  view.onPresentAddCapData((capData) => {
    capData.tag = 'clearable';  // every incoming data can be cleared
    if (capData.payload.Type === CrownHeightmapCap.className) {
      capData.materials = [{
        colorMap: isColorMap(colMap) ? colMap : undefined,  // use a colormap if selected
      }];
      if (capData.payload.Iconics.length > 1) {
        textureBackup = capData.payload.Iconics[1]; // if a texture is send along with the image make a copy so it can be reset after colormap is de-selected via the GUI
      }
      decorationBackup = capData.payload.Decorations;
      if (colMap === 'intensity' && capData.payload.Iconics.length > 1) {
        capData.payload.Iconics[1] = undefined;  // kill intensity texture
      }
      if (!isNaN(maxVal)) {
        capData.payload.Decorations = [{class: 'View.ImageDecoration', data: {DataRange: {Min: 0, Max: maxVal}}}];
      } else {
        capData.payload.Decorations = decorationBackup;
      }
      lastHeightmap = capData;
    }
    director.handleCapData(capData);
  }); // put shape into the scene

  // load and display an offline heightmap
  const loader = new FileLoader();
  loader.setResponseType('arraybuffer');
  const msgPackDecoder = new MsgPackDecoder();

  loader.load('./resources/dist.msgpack', (dbuf: any) => {
    const distImg = msgPackDecoder.decode(dbuf as any);
    loader.load('./resources/intens.msgpack', (ibuf: any) => {
      const intensImg = msgPackDecoder.decode(ibuf as any);
      const capData: CapDataDesc<CrownViewPresentAddDesc<CrownImageDesc, CrownImageDecorationDesc>> = {
        payload: {
          Iconics: [distImg, intensImg],
          Decorations: [{class: 'View.ImageDecoration', data: {DataRange: {Min: 0, Max: 65535}}}],
          Type: CrownHeightmapCap.className,
        },
        name: 'demo',
        materials: [{}],
      };
      lastHeightmap = capData;
      textureBackup = capData.payload.Iconics[1];  // in case a colormap is applied the texture is removed
      addHeightmapToScene(capData);
    });
  });

  function addHeightmapToScene(capData: CapDataDesc) {
    capData.materials[0].colorMap = isColorMap(colMap) ? colMap : undefined;
    if (colMap === 'intensity' && capData.payload.Iconics.length > 1) {
      capData.payload.Iconics[1] = undefined;  // kill intensity texture
    } else {
      capData.payload.Iconics[1] = textureBackup;
    }
    if (!isNaN(maxVal)) {
      capData.payload.Decorations = [{class: 'View.ImageDecoration', data: {DataRange: {Min: 0, Max: maxVal}}}];
    } else {
      capData.payload.Decorations = decorationBackup;
    }
    stage.clearObject(stage.getObjectIdByName(capData.name));
    director.handleWithCapability(CrownCapNames.CROWNHEIGHTMAP, capData);
  }
});
